import { Children, isValidElement, useMemo, useState, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Shared markdown rendering for the Game Knowledge Base. Adds:
 *  - horizontally scrollable, styled tables,
 *  - code blocks rendered in a titled panel with a Copy button + language tag.
 * Inline code keeps the default rendering.
 */

function nodeToText(node: ReactNode): string {
  if (node == null || node === false) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  if (isValidElement(node)) return nodeToText((node.props as { children?: ReactNode }).children);
  return '';
}

function CodePanel({ children }: { children: ReactNode }) {
  const [copied, setCopied] = useState(false);
  // `children` is the inner <code> element produced by react-markdown.
  const codeEl = Children.toArray(children).find(isValidElement);
  const className =
    codeEl && isValidElement(codeEl)
      ? ((codeEl.props as { className?: string }).className ?? '')
      : '';
  const langMatch = /language-([\w+-]+)/.exec(className);
  const lang = langMatch ? langMatch[1] : 'code';
  const text = nodeToText(children);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable; ignore
    }
  };

  return (
    <div className="md-code">
      <div className="md-code-bar">
        <span className="md-code-lang">{lang}</span>
        <button type="button" className="md-code-copy" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre>{children}</pre>
    </div>
  );
}

const components: Components = {
  pre: ({ children }) => <CodePanel>{children}</CodePanel>,
  table: ({ children }) => (
    <div className="md-table-wrap">
      <table>{children}</table>
    </div>
  ),
};

export function MarkdownView({ children }: { children: string }) {
  return (
    <div className="md-body">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}

interface Section {
  title: string;
  body: string;
}

/**
 * Splits a markdown document into top-level (`# `) sections, ignoring `#`
 * lines that fall inside fenced code blocks (e.g. `# comment.java`).
 */
function splitSections(md: string): { preamble: string; sections: Section[] } {
  const lines = md.split('\n');
  const preamble: string[] = [];
  const sections: Section[] = [];
  let current: Section | null = null;
  let inFence = false;

  for (const line of lines) {
    const trimmed = line.trimStart();
    if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) {
      inFence = !inFence;
    }
    const heading = !inFence ? /^#\s+(.+?)\s*$/.exec(line) : null;
    if (heading) {
      if (current) sections.push(current);
      current = { title: heading[1], body: '' };
    } else if (current) {
      current.body += (current.body ? '\n' : '') + line;
    } else {
      preamble.push(line);
    }
  }
  if (current) sections.push(current);
  return {
    preamble: preamble.join('\n').trim(),
    sections: sections.map((s) => ({ title: s.title, body: s.body.trim() })),
  };
}

/**
 * Renders a long markdown report as a set of collapsible sections (one per
 * top-level heading) with Expand all / Collapse all controls. Falls back to
 * a plain rendering when the report has no top-level headings.
 */
export function ReportView({ markdown }: { markdown: string }) {
  const { preamble, sections } = useMemo(() => splitSections(markdown), [markdown]);
  const [open, setOpen] = useState<boolean[]>(() => sections.map(() => true));

  // Keep the open-state array in sync if the section count changes
  // (e.g. after a regenerate produces a different structure).
  if (open.length !== sections.length) {
    setOpen(sections.map(() => true));
  }

  if (sections.length === 0) {
    return <MarkdownView>{markdown}</MarkdownView>;
  }

  const toggle = (i: number) =>
    setOpen((prev) => prev.map((v, idx) => (idx === i ? !v : v)));
  const setAll = (value: boolean) => setOpen(sections.map(() => value));

  return (
    <div className="report-view">
      <div className="report-toolbar">
        <button type="button" className="btn btn-ghost small" onClick={() => setAll(true)}>
          Expand all
        </button>
        <button type="button" className="btn btn-ghost small" onClick={() => setAll(false)}>
          Collapse all
        </button>
      </div>
      {preamble && (
        <div className="report-preamble">
          <MarkdownView>{preamble}</MarkdownView>
        </div>
      )}
      {sections.map((section, i) => (
        <section key={`${section.title}-${i}`} className="report-section">
          <button
            type="button"
            className="report-section-header"
            aria-expanded={open[i]}
            onClick={() => toggle(i)}
          >
            <span className={`report-chevron ${open[i] ? 'open' : ''}`} aria-hidden>
              ▶
            </span>
            <span className="report-section-title">{section.title}</span>
          </button>
          {open[i] && (
            <div className="report-section-body">
              <MarkdownView>{section.body}</MarkdownView>
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
